import { Box, Flex } from '@chakra-ui/react';
import ServicesPage from '../ServicesPage';
import FeesPage from '../FeesPage';
import ContactPage from '../ContactPage';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, useNavigate, useNavigationType } from "react-router-dom";
import { useEffect, useState } from 'react'; 
import { jwtDecode } from "jwt-decode";
import NavBar from "../NavBar";
import { useToast } from "@chakra-ui/react";
import TermsAndConditions from '../TermsAndConditions';
import VictoriaSelectiveEntryYear9Page from '../VictoriaSelectiveEntryYear9Page';
import NSWSelectiveEntryPage from '../NSWSelectiveEntryPage';
import NSWOpportunityClassPage from '../NSWOpportunityClassPage';
import EduTestPage from '../EduTestPage';
import ACERPage from '../ACERPage';
import VictoriaSelectiveEntryYear10Page from '../VictoriaSelectiveEntryYear10Page';


const useTokenExpiration = (
  token: string | null,
  onLogout: () => void
) => {
  const toast = useToast();

  useEffect(() => {
    if (!token) return;

    try {
      const decoded: { exp: number } = jwtDecode(token); // Decode the token to get the expiration time
      const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
      const timeRemaining = decoded.exp - currentTime; // Calculate time left until expiration

      if (timeRemaining > 0) {
        // Show a warning toast 10 minutes before expiration (if timeRemaining is more than 10 minutes)
        if (timeRemaining > 600) {
          const warningTimeout = setTimeout(() => {
            toast({
              title: "Session Expiring Soon",
              description:
                "Your session will expire in 10 minutes. Please save your work.",
              status: "warning",
              duration: 9000, // Toast stays for 9 seconds
              isClosable: true,
            });
          }, (timeRemaining - 600) * 1000); // Trigger 10 minutes before expiration

          // Cleanup warning timeout
          return () => clearTimeout(warningTimeout);
        }

        // Set timeout to logout when token expires
        const logoutTimeout = setTimeout(() => {
          console.log("Token expired. Logging out...");
          onLogout();
        }, timeRemaining * 1000);

        // Cleanup logout timeout
        return () => clearTimeout(logoutTimeout);
      } else {
        onLogout(); // Token already expired, logout immediately
      }
    } catch (err) {
      console.error("Failed to decode token:", err);
      onLogout(); // In case of error, consider the token invalid and logout
    }
  }, [token, onLogout, toast]);
};


interface TokenData {
  sub: string;
  role_id: number;
  tenant_id: string;
  is_initial_password: boolean;
}

// Utility function to decode token
const decodeToken = (token: string | null): TokenData | null => {
  try {
      return token ? jwtDecode<TokenData>(token) : null;
  } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
  }
};


// Resets scroll to the top whenever the coaching page changes.
// - Skips when the URL has a #hash, so in-page anchors keep working.
// - Skips browser Back/Forward (POP), so the browser can restore the previous position.
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (hash) return;
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const CoachingRouter = () => {

    const navigate = useNavigate();
    const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
    const [userInitials, setUserInitials] = useState<string | null>(null);

    // Decode token to get the `is_initial_password` flag
    const decodedToken = decodeToken(token);
    const isInitialPassword = decodedToken?.is_initial_password ?? false;
  
    useEffect(() => {
      if (token) {
        try {
          const decoded: { sub: string } = jwtDecode(token); // Decode JWT
          const username = decoded.sub || ""; // Default to an empty string if sub is undefined
  
          // Extract initials (first and last character or first two letters)
          const initials =
          username.length > 1
            ? (username[0] + username[username.length - 1]).toUpperCase()
            : username[0]?.toUpperCase() || "";
  
          setUserInitials(initials || decoded.sub); // Use initials or full name
        } catch (err) {
          console.error("Failed to decode token:", err);
          setUserInitials(null);
        }
      } else {
        setUserInitials(null);
      }
    }, [token]);
  
    const handleLogin = (newToken: string) => {
      localStorage.setItem("token", newToken);
      setToken(newToken);
    };
  
    const handleLogout = () => {
      localStorage.removeItem("token");
      setToken(null);
      setUserInitials(null);
      navigate("/platform/eduvic/login");
    };
  
    const isAuthenticated = !!token; 

    
  
    useTokenExpiration(token, handleLogout);
  

    return (
        <Box >
        <NavBar
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        userInitials={userInitials}
        />
           
          {/* Main content area with flexible width */}
          <Box overflowY="auto">
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Navigate to="services" />} />
              <Route path="services" element={<ServicesPage />} />
              <Route path="fees" element={<FeesPage />} />
              <Route path="contact" element={<ContactPage />} />
              <Route path="termsandconditions" element={<TermsAndConditions />} />
              <Route path="domain/vic-year10" element={<VictoriaSelectiveEntryYear10Page />} />
              <Route path="domain/vic-year9" element={<VictoriaSelectiveEntryYear9Page />} />
              <Route path="domain/nsw-year7" element={<NSWSelectiveEntryPage />} />
              <Route path="domain/nsw-oc-year5" element={<NSWOpportunityClassPage />} />
              <Route path="domain/edutest" element={<EduTestPage />} />
              <Route path="domain/acer" element={<ACERPage />} />

            </Routes>
          </Box>
          
      </Box>
    );
  };

export default CoachingRouter;
